import React, { useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float } from '@react-three/drei';
import * as THREE from 'three';

const Propeller = ({ position }: { position: [number, number, number] }) => {
  const propRef = useRef<THREE.Group>(null);

  useFrame((_, delta) => {
    if (propRef.current) {
      propRef.current.rotation.y += delta * 20;
    }
  });

  return (
    <group position={position}>
      {/* Protective ring */}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.4, 0.04, 8, 24]} />
        <meshStandardMaterial color="#ffffff" metalness={0.2} roughness={0.2} />
      </mesh>
      {/* Rotor */}
      <group ref={propRef}>
        <mesh position={[0, 0.05, 0]}>
          <boxGeometry args={[0.7, 0.02, 0.05]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>
      </group>
      {/* Motor hub */}
      <mesh position={[0, 0, 0]}>
        <cylinderGeometry args={[0.08, 0.08, 0.12, 10]} />
        <meshBasicMaterial color="#ffffff" />
      </mesh>
    </group>
  );
};

const DroneGeometry = () => (
  <>
    {/* Core Body - navy to black gradient via lighting */}
    <mesh>
      <sphereGeometry args={[0.3, 32, 32]} />
      <meshStandardMaterial color="#0f2d5e" metalness={0.1} roughness={0.4} />
    </mesh>

    {/* Inner Glowing Core */}
    <mesh>
      <sphereGeometry args={[0.15, 12, 12]} />
      <meshBasicMaterial color="#4fc3f7" toneMapped={false} />
    </mesh>

    {/* Arms */}
    <mesh position={[0.4, 0, 0.4]} rotation={[0, -Math.PI / 4, 0]}>
      <boxGeometry args={[0.8, 0.05, 0.1]} />
      <meshStandardMaterial color="#ffffff" metalness={0.2} roughness={0.2} />
    </mesh>
    <mesh position={[-0.4, 0, -0.4]} rotation={[0, -Math.PI / 4, 0]}>
      <boxGeometry args={[0.8, 0.05, 0.1]} />
      <meshStandardMaterial color="#ffffff" metalness={0.2} roughness={0.2} />
    </mesh>
    <mesh position={[-0.4, 0, 0.4]} rotation={[0, Math.PI / 4, 0]}>
      <boxGeometry args={[0.8, 0.05, 0.1]} />
      <meshStandardMaterial color="#ffffff" metalness={0.2} roughness={0.2} />
    </mesh>
    <mesh position={[0.4, 0, -0.4]} rotation={[0, Math.PI / 4, 0]}>
      <boxGeometry args={[0.8, 0.05, 0.1]} />
      <meshStandardMaterial color="#ffffff" metalness={0.2} roughness={0.2} />
    </mesh>

    {/* Propellers */}
    <Propeller position={[0.7, 0.1, 0.7]} />
    <Propeller position={[-0.7, 0.1, -0.7]} />
    <Propeller position={[-0.7, 0.1, 0.7]} />
    <Propeller position={[0.7, 0.1, -0.7]} />
  </>
);

const DroneSoccerGeometry = () => (
  <group>
    <DroneGeometry />
    {/* Soccer Ball Cage */}
    <mesh rotation={[0, 0, 0]}>
      <torusGeometry args={[1.2, 0.03, 6, 32]} />
      <meshStandardMaterial color="#ffffff" metalness={0.2} roughness={0.1} />
    </mesh>
    <mesh rotation={[Math.PI / 2, 0, 0]}>
      <torusGeometry args={[1.2, 0.03, 6, 32]} />
      <meshStandardMaterial color="#ffffff" metalness={0.2} roughness={0.1} />
    </mesh>
    <mesh rotation={[0, Math.PI / 2, 0]}>
      <torusGeometry args={[1.2, 0.03, 6, 32]} />
      <meshStandardMaterial color="#ffffff" metalness={0.2} roughness={0.1} />
    </mesh>
  </group>
);

const DroneModel = () => {
  const droneRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    if (droneRef.current) {
      const t = state.clock.elapsedTime;
      const entryTime = 1.5;

      if (t < entryTime) {
        const progress = t / entryTime;
        const ease = 1 - Math.pow(1 - progress, 4);

        droneRef.current.position.x = 8 * (1 - ease);
        droneRef.current.position.y = 5 * (1 - ease);
        droneRef.current.position.z = -15 * (1 - ease);

        droneRef.current.rotation.y = -Math.PI / 2 * (1 - ease) - Math.PI / 6;
        droneRef.current.rotation.z = Math.PI / 4 * (1 - ease);
        droneRef.current.rotation.x = Math.PI / 8 * (1 - ease);
      } else {
        const hoverT = t - entryTime;
        droneRef.current.position.x = Math.sin(hoverT * 0.5) * 2.5;
        droneRef.current.position.y = Math.sin(hoverT * 0.6) * 1;
        droneRef.current.position.z = Math.sin(hoverT * 0.4) * 1.5;

        droneRef.current.rotation.y = Math.sin(hoverT * 0.4) * 0.4 - Math.PI / 6;
        droneRef.current.rotation.x = Math.cos(hoverT * 0.5) * 0.2;
        droneRef.current.rotation.z = -Math.sin(hoverT * 0.5) * 0.25;
      }
    }
  });

  return (
    <group ref={droneRef}>
      <DroneGeometry />
    </group>
  );
};

export const BackgroundDrone = ({ offset, speed, scale = 0.5 }: { offset: number, speed: number, scale?: number }) => {
  const droneRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    if (droneRef.current) {
      const t = state.clock.elapsedTime * speed + offset;
      const raw = (t % 20) / 20;
      const x = 8 - raw * 16;

      droneRef.current.position.x = x;
      droneRef.current.position.y = Math.sin(t * 1.5) * 1.2 + (offset % 4) - 2;
      droneRef.current.position.z = Math.cos(t) * 3 - 2;

      droneRef.current.rotation.y = -Math.PI / 2;
      droneRef.current.rotation.x = Math.sin(t * 3) * 0.2;
      droneRef.current.rotation.z = Math.cos(t * 2) * 0.2;
    }
  });

  return (
    <group ref={droneRef} scale={scale}>
      <DroneSoccerGeometry />
    </group>
  );
};

interface Drone3DProps {
  className?: string;
  showMain?: boolean;
  showBackground?: boolean;
}

export const Drone3D: React.FC<Drone3DProps> = ({ className, showMain = true, showBackground = true }) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div className={`w-full h-full min-h-[400px] ${className || ''}`} style={{ pointerEvents: 'auto' }}>
      <Canvas
        camera={{ position: [0, 1.5, 4], fov: isMobile ? 60 : 45 }}
        dpr={1}
        gl={{ antialias: false, powerPreference: 'high-performance', precision: 'lowp' }}
        performance={{ min: 0.5 }}
      >
        <ambientLight intensity={0.8} />
        <directionalLight position={[8, 8, 5]} intensity={2.5} />
        <directionalLight position={[-6, -4, -4]} intensity={0.1} color="#0a1a3a" />

        {showMain && (
          <Float speed={2} rotationIntensity={0.5} floatIntensity={1} floatingRange={[-0.2, 0.2]}>
            <group scale={isMobile ? 0.6 : 0.85}>
              <DroneModel />
            </group>
          </Float>
        )}
        
        {showBackground && (
          isMobile ? (
            <BackgroundDrone offset={0} speed={0.8} scale={0.25} />
          ) : (
            <>
              <BackgroundDrone offset={0} speed={0.8} scale={0.25} />
              <BackgroundDrone offset={5} speed={0.6} scale={0.2} />
              <BackgroundDrone offset={12} speed={0.7} scale={0.3} />
              <BackgroundDrone offset={18} speed={0.5} scale={0.15} />
            </>
          )
        )}
      </Canvas>
    </div>
  );
};
